/**
 * Epic 9 — the Tasks Inbox (US 9.1), Replies (US 9.2) and Assigned comments
 * (US 9.3). Everything lives under the "me" key, so live sync refreshes it.
 */
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_CALENDAR_PREFIX, SuiteApiClient } from "@weco/suite-client";
import { call, type Comment, type Priority } from "../task/api";
import { ME_KEY } from "../personal/keys";

const client = new SuiteApiClient(API_CALENDAR_PREFIX);

export type InboxKind = "mention" | "assignment" | "comment" | "due" | "overdue" | "status" | "review" | "attachment" | "reply" | "system" | "reminder" | "approval" | "mochi";
export type InboxFilter = "all" | "unread" | "read" | "assigned" | "mentions" | "approvals" | "mochi" | "due_today" | "overdue" | "high" | "done" | "archived";
export type InboxSort = "newest" | "oldest" | "unread" | "priority";
export const INBOX_FILTERS: InboxFilter[] = ["all", "unread", "read", "assigned", "mentions", "approvals", "mochi", "due_today", "overdue", "high", "done", "archived"];
export const INBOX_SORTS: InboxSort[] = ["newest", "oldest", "unread", "priority"];

export interface InboxTask {
  id: string;
  key: string;
  title: string;
  priority: Priority;
  done: boolean;
  due_date: string | null;
  squad_name: string;
}

export interface InboxItem {
  id: string;
  kind: InboxKind;
  /** The server's own wording — used where the kind alone can't say it. */
  title: string;
  text: string;
  task: InboxTask | null;
  task_title: string;
  /** False when I can no longer open the task (US 9.1 failure criterion). */
  accessible: boolean;
  comment_id: string | null;
  /** US 10.4 — the settings row this item belongs to. */
  event?: string;
  /** US 10.1 — events grouped into this item (within 10 minutes). */
  group_count?: number;
  /** US 5.5 — an open approval request I can still decide. */
  approval?: { id: string } | null;
  /** US 13.6 — where an item about no task yet opens (Mochi's meeting suggestions). */
  route?: string | null;
  actor: { email: string; name: string };
  read: boolean;
  pinned: boolean;
  archived: boolean;
  created_at: string;
}

export interface InboxPage {
  results: InboxItem[];
  has_more: boolean;
  unread: number;
  total: number;
}

export type InboxAction = "read" | "unread" | "pin" | "unpin" | "archive" | "unarchive" | "delete";

export interface ReplyRow extends InboxItem {
  root_id?: string;
  reply?: Comment;
  thread: Comment[];
}

export interface AssignedComment extends Comment {
  task: InboxTask | null;
  assigned_to: { email: string; name: string };
  assigned_at: string | null;
  resolved_by: string;
}

export interface AssignedQuery {
  tab: "assigned" | "delegated";
  priority?: Priority | "";
  status?: "open" | "resolved" | "all";
  range?: "all" | "today" | "7d" | "30d" | "custom";
  from?: string | null;
  to?: string | null;
  q?: string;
}

const qs = (o: Record<string, unknown>) =>
  Object.entries(o)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join("&");

export const inboxApi = {
  list: (filter: InboxFilter, sort: InboxSort, offset = 0, limit = 6) => call(() => client.get<InboxPage>(`/inbox/?${qs({ filter, sort, offset, limit })}`)),
  /** Marks it read and says where to go — or refuses (403) if I can't open the task any more. */
  open: (id: string) => call(() => client.post<{ task: InboxTask | null; route?: string; comment_id: string | null }>(`/inbox/${id}/open/`, {})),
  bulk: (ids: string[], action: InboxAction) => call(() => client.post<{ updated: number; unread: number; total: number }>("/inbox/bulk/", { ids, action })),
  readAll: () => call(() => client.post<{ updated: number }>("/inbox/read-all/", {})),
  clearRead: () => call(() => client.post<{ removed: number }>("/inbox/clear-read/", {})),
  replies: (tab: "unread" | "read", offset = 0, limit = 20) =>
    call(() => client.get<{ results: ReplyRow[]; has_more: boolean; counts: { unread: number; read: number } }>(`/inbox/replies/?${qs({ tab, offset, limit })}`)),
  assigned: (q: AssignedQuery) => call(() => client.get<{ results: AssignedComment[] }>(`/assigned-comments/?${qs({ ...q })}`)),
  assignedBulk: (ids: string[], action: "resolve" | "unresolve" | "archive" | "delete") =>
    call(() => client.post<{ updated: string[]; failed: { id: string; detail: string }[] }>("/assigned-comments/bulk/", { ids, action })),
};

const INBOX_KEY = [...ME_KEY, "inbox"] as const;

export function useInbox(filter: InboxFilter, sort: InboxSort) {
  return useInfiniteQuery({
    queryKey: [...INBOX_KEY, "list", filter, sort],
    queryFn: ({ pageParam }) => inboxApi.list(filter, sort, pageParam, 6),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (last.has_more ? pages.reduce((n, p) => n + p.results.length, 0) : undefined),
  });
}

/** The sidebar's "5 unread" (US 8.1) — the Inbox's own count. */
export function useInboxCounts() {
  return useQuery({ queryKey: [...INBOX_KEY, "counts"], queryFn: () => inboxApi.list("all", "newest", 0, 1), select: (p) => ({ unread: p.unread, total: p.total }) });
}

export function useReplies(tab: "unread" | "read") {
  return useInfiniteQuery({
    queryKey: [...INBOX_KEY, "replies", tab],
    queryFn: ({ pageParam }) => inboxApi.replies(tab, pageParam, 20),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (last.has_more ? pages.reduce((n, p) => n + p.results.length, 0) : undefined),
  });
}

export function useAssignedComments(q: AssignedQuery) {
  return useQuery({ queryKey: [...INBOX_KEY, "assigned", q], queryFn: () => inboxApi.assigned(q) });
}

export function useRefreshInbox() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: INBOX_KEY });
}

// ── US 10.4 — notification settings ────────────────────────────────────────

export interface NotificationEventRow {
  key: string;
  label: string;
  locked: boolean;
  inbox: boolean;
  email: boolean;
  push: boolean;
}
export interface NotificationSettings {
  events: NotificationEventRow[];
  digest_enabled: boolean;
  digest_time: string;
  time_zone: string;
  quiet_enabled: boolean;
  quiet_start: string;
  quiet_end: string;
  paused_until: string | null;
  muted: { id: string; name: string; kind: "hub" }[];
}
export type NotificationSettingsPatch = Partial<Omit<NotificationSettings, "events" | "muted">> & {
  events?: Record<string, Partial<Record<"inbox" | "email" | "push", boolean>>>;
};
export const NOTIFY_SETTINGS_KEY = ["tasks", "notification-settings"] as const;
export const notifySettingsApi = {
  get: () => call(() => client.get<NotificationSettings>("/notification-settings/")),
  save: (body: NotificationSettingsPatch) => call(() => client.patch<NotificationSettings>("/notification-settings/", body)),
};
